import React, { useState, useEffect, useRef } from "react";
import PageContainer from "../components/pages/PageContainer";
import PageHeaderTitle from "../components/pages/PageHeaderTitle";
import DashboardHeader from "../components/Other/DashboardHeader";
import { useTranslation } from "react-i18next";
import { marketSentiment, sentimentReportApi, voteMarket } from "../api/user";
import { toast } from "react-toastify";
import { useOutletContext } from "react-router-dom";
import {
  AlternatingFillAreaSeries,
  Chart,
  ChartCanvas,
  StraightLine,
  XAxis,
  YAxis,
  discontinuousTimeScaleProviderBuilder,
} from "react-financial-charts";
import { format } from "d3-format";
import moment from "moment-timezone";
import $ from "jquery";

const MarketSentimentPage = () => {
  const [sidebarToggle] = useOutletContext();
  const isMobileView = document.body.clientWidth <= 768;
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [voteType, setVoteType] = useState("");
  const [Bull, setBull] = useState([]);
  const [hasVoted, setHasVoted] = useState("");
  const height = window.innerHeight * 0.3;
  const [marketSentimentData, setMarketSentimentData] = useState([]);
  const containerRef = useRef();
  const [containerWidth, setContainerWidth] = useState(
    containerRef.current ? containerRef.current.clientWidth : 0
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.target === containerRef.current) {
          setContainerWidth(entry.contentRect.width);
        }
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef]);

  useEffect(() => {
    marketSentimentReport();
  }, []);

  const marketSentimentReport = async () => {
    const res = await sentimentReportApi();
    if (res?.data?.status === 200) {
      setMarketSentimentData(res?.data?.data);
    }
  };

  const calculateChartWidth = () => {
    const containerWidth = $(".sentiment-block")[0];
    if (isMobileView) {
      return containerWidth?.clientWidth + 24;
    }
    return containerWidth?.clientWidth;
  };

  const width = calculateChartWidth();

  let initialData =
    marketSentimentData && marketSentimentData.length > 0
      ? marketSentimentData
      : [];

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const response = await marketSentiment();
        if (response) {
          setLoading(false);
          setBull(response.data.data);
          setHasVoted(response.data.data.hasVoted);
        } else {
          // Handle the case when the response is falsy
          setBull({}); // Initialize Bull with an empty object
          setHasVoted(false);
          setLoading(false);
        }
      } catch (error) {
        setLoading(false);
        console.error("Error:", error);
        setBull({}); // Initialize Bull with an empty object
        setHasVoted(false);
      }
    };
    fetchPost();
  }, []);

  const openModal = (type) => {
    if (hasVoted) {
      toast.error("You have already voted this week");
      setShowModal(false);
    } else {
      setVoteType(type);
      setShowModal(true);
    }
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const handleVote = async () => {
    let voteValue;
    if (voteType === "BULL") {
      voteValue = "1";
    } else if (voteType === "BEAR") {
      voteValue = "2";
    } else {
      // Handle unexpected vote types
      console.error(`Unexpected vote type: ${voteType}`);
      return;
    }
    try {
      const response = await voteMarket(voteValue);
      if (response.status === 200) {
        // console.log(response, "aaa");
        const bull = {
          bull: response.data.data.bull,
          bear: response.data.data.bear,
        };
        setBull(bull);
        setHasVoted(response.data.data.hasVoted);
        toast.success(response.data.message);
      }
    } catch (error) {
      console.error("Error voting:", error);
      toast.error("You have already voted this week");
    }
    setShowModal(false);
  };

  const formattedData = initialData.map((item) => ({
    date: new Date(item.report_date),
    bull: item.bull,
  }));

  const yAccessor = (d) => d.bull;

  const ScaleProvider =
    discontinuousTimeScaleProviderBuilder().inputDateAccessor(
      (d) => new Date(d.date)
    );

  let { data, xScale, yScale, xAccessor, displayXAccessor } =
    ScaleProvider(formattedData);

  const max = xAccessor(data[data.length - 1]);
  const min = xAccessor(data[0]);

  let xExtents = [min, max];

  const lastObject = marketSentimentData[marketSentimentData.length - 1];

  return (
    <>
      <DashboardHeader toggle={sidebarToggle} />
      <PageHeaderTitle title="Market Sentiment" />
      <PageContainer>
        <div className={containerWidth >= 1000 ? "flex justify-center" : ""}>
          <div
            ref={containerRef}
            id="main-container"
            className={
              isMobileView
                ? "mb-24 py-8 px-2 rounded-2xl border bg-white border-white-500 max-w-[55rem] mx-auto h-auto"
                : " p-8 rounded-2xl border bg-white mt-4 border-white-500 max-w-[70rem] ml-[4rem] mr-8 mx-auto h-auto mb-2"
            }
          >
            <div className="sentiment-block">
              <div className="flex justify-center rounded-md md:px-5 px-4 ">
                <button
                  type="button"
                  onClick={() => openModal("BULL")}
                  style={{
                    fontWeight: "bold",
                    width: "100%",
                    padding: "1.25rem 0",
                    fontSize: "0.875rem",
                    textAlign: "center",
                    zIndex: "9",
                    outline: "none",
                    transition:
                      "background-color 0.15s ease-in-out, border-color 0.15s ease-in-out",
                    backgroundColor: "#4CAF50",
                    color: "white",
                    // border: "1px solid #1B5E20",
                    borderRadius: "0.5rem 0 0 0.5rem",
                  }}
                >
                  {t("BULL")} {hasVoted ? Bull?.bull + "%" : ""}
                </button>

                <button
                  type="button"
                  onClick={() => openModal("BEAR")}
                  style={{
                    fontWeight: "bold",
                    width: "100%",
                    padding: "1.25rem 0",
                    fontSize: "0.875rem",
                    textAlign: "center",
                    zIndex: "9",
                    outline: "none",
                    transition:
                      "background-color 0.15s ease-in-out, border-color 0.15s ease-in-out",
                    backgroundColor: "#F44336",
                    color: "white",
                    // border: "1px solid #B71C1C",
                    borderRadius: "0 0.5rem 0.5rem 0",
                  }}
                >
                  {t("BEAR")} {hasVoted ? Bull?.bear + "%" : ""}
                </button>
              </div>

              {showModal && (
                <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex justify-center items-center">
                  <div className="bg-white p-6 rounded-lg shadow-xl">
                    <div className="flex justify-center items-center my-2">
                      <img
                        src={"/qushi_logo.png"}
                        className="h-24"
                        alt="logo"
                      />
                    </div>
                    <h2 className="text-xl font-bold mb-4">
                      {t("Are you sure you want to vote")} {voteType}?
                    </h2>
                    <div className="flex justify-center space-x-4">
                      <button
                        onClick={handleVote}
                        className="px-4 w-24 py-2 bg-primaryColor text-white font-semibold rounded hover:bg-green-600 focus:outline-none focus:bg-green-600"
                      >
                        {t("Yes")}
                      </button>
                      <button
                        onClick={closeModal}
                        className="px-4 py-2 w-24 bg-gray-300 text-gray-800 font-semibold rounded hover:bg-gray-400 focus:outline-none focus:bg-gray-400"
                      >
                        {t("Cancel")}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <div className="text-center text-xs mt-3 text-gray-500 ">
                <p>Voting Period Opens Friday At 4:00PM ET</p>
              </div>
              <div className=" text-center bg-white mt-8 md:px-5 px-4">
                <div className="text-center md:text-left md:ml-7 pt-5">
                  <p className="font-bold text-center ">
                    {t("US_Investor_Sentiment,_%Bullish_(I:USISBNW)")}
                  </p>
                  <p className="font-bold text-center text-xs mt-1">
                    {lastObject?.bull}% for WK of{" "}
                    {moment(lastObject?.report_date).format("MMM DD, YYYY")}
                  </p>
                </div>

                <h4 className=" font-bold text-primaryColor mt-2">
                  {t("Level_Chart")}
                </h4>

                <div
                  id="container"
                  className="flex items-center justify-center align-center mt-4"
                  style={{ marginLeft: -12, width: width }}
                >
                  <ChartCanvas
                    height={height}
                    ratio={3}
                    width={width}
                    padding={{ left: 0, right: 0 }}
                    data={data}
                    displayXAccessor={displayXAccessor}
                    seriesName="Data"
                    xScale={xScale}
                    xAccessor={xAccessor}
                    xExtents={xExtents}
                    disableZoom={true}
                    disablePan={true}
                    disableInteraction={true}
                  >
                    <Chart id={1} yExtents={yAccessor}>
                      <XAxis
                        strokeStyle={"#8d8d8d"}
                        tickStrokeStyle={"#8d8d8d"}
                        ticks={isMobileView ? 6 : 12}
                      />
                      <YAxis
                        strokeStyle={"#8d8d8d"}
                        tickStrokeStyle={"#8d8d8d"}
                        tickFormat={format(".0f")}
                        ticks={5}
                      />
                      <XAxis
                        strokeStyle={"#8d8d8d"}
                        tickStrokeStyle={"#8d8d8d"}
                        showTicks={false}
                        xZoomHeight={1}
                        tickPadding={0}
                        axisAt={"top"}
                        orient={"top"}
                      />
                      <YAxis
                        strokeStyle={"#8d8d8d"}
                        tickStrokeStyle={"#8d8d8d"}
                        showTicks={false}
                        yZoomWidth={1}
                        tickPadding={0}
                        axisAt={"left"}
                        orient={"left"}
                      />
                      <AlternatingFillAreaSeries
                        yAccessor={yAccessor}
                        baseAt={0}
                        fillStyle={{ top: "#709a76", bottom: "#b07673" }}
                        strokeStyle={{ top: "#24422b", bottom: "#5a4848" }}
                      />
                      <StraightLine yValue={0} lineDash="Solid" />
                    </Chart>
                  </ChartCanvas>
                </div>

                <h4 className=" mt-7 font-bold text-primaryColor">
                  {t("Basic_Info")}
                </h4>

                <p className="mt-3 text-gray-500 text-sm">
                  {t(
                    "QUSHi's Market Sentiment is a weekly poll of QUSHi users insight into the bullishness of the overall market. It is modeled after the % Bullish indicator from The American Association of Individual Investors (AAII) Sentiment Survey. The (AAII) poll is a popular tool used by investors in various ways, one of which is predicting bubbles in the market."
                  )}
                </p>
                <p className=" mt-5 font-bold text-primaryColor">
                  {t("Stats")}
                </p>

                <div
                  className={
                    isMobileView
                      ? "flex justify-evenly mt-2"
                      : "flex justify-evenly"
                  }
                >
                  <div className="flex flex-col justify-start">
                    <p className="text-start text-gray-500">
                      {t("Last_Value")}
                    </p>
                    <p className="text-start text-gray-500 ">
                      {t("Last_Update")}{" "}
                    </p>
                  </div>
                  <div
                    className={
                      isMobileView
                        ? "flex flex-col justify-end ml-8"
                        : "flex flex-col justify-end"
                    }
                  >
                    <p className="text-end">{lastObject?.bull}%</p>
                    <p className="text-end">
                      {moment(lastObject?.report_date).format("MMM DD, YYYY")}{" "}
                      ET
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageContainer>
    </>
  );
};

export default MarketSentimentPage;
